import { findUnsupportedCardMethods, isMethodSupported, isWalletNative, resolveWalletSandbox } from './utils'
import { addNotification } from 'containers/Notifications/notificationsSlice'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getWalletAdapter } from './adapters/wallet'
import { useReduxDispatch } from 'store'

/**
 * Jedno miejsce decydujące, których metod NIE pokazujemy na tym urządzeniu.
 * Google Pay odpowiada asynchronicznie (isReadyToPay), więc do czasu odpowiedzi
 * jego kafelek jest ukryty - lepiej pokazać go z opóźnieniem niż pokazać i schować.
 */
const useAvailableMethods = (methods?: payments.PaymentMethod[]) => {
  const [walletAvailability, setWalletAvailability] = useState<Record<string, boolean>>({})
  const reported = useRef<Record<string, boolean>>({})

  const dispatch = useReduxDispatch()

  // Operator z formularzem karty, dla którego nie mamy adaptera, to błąd konfiguracji -
  // nie da się go obsłużyć, ale nie ma prawa przejść niezauważony.
  useEffect(() => {
    findUnsupportedCardMethods(methods).forEach(method => {
      const provider = method.cardForm?.provider || 'nieznany'
      if (reported.current[provider]) return
      reported.current[provider] = true

      console.error(
        `[payments] Brak adaptera karty dla operatora "${provider}" - metoda "${method.name}" jest ukryta. ` +
        'Dopisz src/payments/adapters/card/' + provider + '.ts (kontrakt: adapters/types.ts) i wpis w cardAdapters, ' +
        'albo poproś backend o cardForm.mode inny niż "embedded" - wtedy front obsłuży ją jak zwykłe przekierowanie.'
      )

      if (process.env.NODE_ENV === 'development') {
        dispatch(addNotification({
          type: 'error',
          translate: false,
          message: `Brak adaptera karty dla operatora "${provider}" - metoda ukryta`
        }))
      }
    })
  }, [methods, dispatch])

  useEffect(() => {
    const googleMethods = (methods || []).filter(method => method.key === 'gpay' && isWalletNative(method))
    if (googleMethods.length === 0) return

    let alive = true
    Promise.all(googleMethods.map(async method => {
      const sandbox = resolveWalletSandbox(method.walletForm, methods)
      // getWalletAdapter (nie stały walletAdapters.gpay!) - w natywnej appce zwraca adapter
      // mostu do appki, a jego isAvailable czyta window.__NATIVE_APP__.wallets zamiast pytać
      // web-owe SDK Google, które w WebView i tak nie otwiera natywnego arkusza.
      const isAvailable = await getWalletAdapter(method.walletForm as payments.WalletForm)
        ?.isAvailable(method.walletForm as payments.WalletForm, { sandbox })
        .catch(() => false) ?? false
      return [String(method.id), isAvailable] as const
    })).then(entries => {
      if (alive) setWalletAvailability(Object.fromEntries(entries))
    })

    return () => {
      alive = false
    }
  }, [methods])

  return useMemo(() => methods?.filter(method => {
    // Filtr leci w trakcie renderu i sięga do SDK przeglądarek, które potrafią rzucić
    // (Apple Pay na http robi to do dziś). Wyjątek tutaj zabrałby cały ekran płatności,
    // więc w najgorszym razie chowamy jedną metodę i zostawiamy ślad w konsoli.
    try {
      if (!isMethodSupported(method)) return false
      if (method.key === 'gpay' && isWalletNative(method)) return walletAvailability[String(method.id)] === true
      return true

    } catch (error) {
      console.error('[payments] sprawdzenie dostępności metody', method?.key, 'rzuciło wyjątkiem', error)
      return false
    }
  }), [methods, walletAvailability])
}

export default useAvailableMethods
